/**
 * Promo Auto MM ([mm], Jordy 20-10-2026). Een aparte promo-stroom bovenop de
 * auto-MM-motor: afzenders (ook verborgen/inactieve modellen) sturen hun volgers
 * een bericht dat een actief model promoot, met diens link en het cadeau dat dat
 * model bij een follow stuurt. Generate -> review/approve -> Send approved; de
 * gewone verzender doet de rest. Alleen owner/admin (server: _vereis_beheer).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Empty, ErrorNote, Loading, Panel, Seg, Table, Tag } from '../components/ui'
import { useApi, usePoll } from '../lib/useApi'
import { useActions } from '../lib/actions'
import { api } from '../lib/api'
import { useMijnZone } from '../lib/tijdzone'
import { TijdKiezer } from '../components/TijdKiezer'
import { standaardMoment, tijdFout, type DatumTijd } from '../lib/tijdkeuze'
import { alsZone, zoneDelen, zoneLabel, zoneNaarUtc } from '../lib/amsterdam'
import { Field, Modal } from '../components/Modal'
import { DOELGROEPEN, FAN_ACTIEF, RECENT, UITSLUITINGEN, heeftFilters, methodeNaam } from '../components/MassDmFormulier'
import { Overzicht, type GeplandRij } from './MassMessages'

const P = '/api/admin/mass-promo'

interface Kandidaat {
  slug: string; name: string; platform: string; active: boolean; hidden: boolean; removed: boolean
  followers: number | null; last_sent: string | null; usable: boolean; reason: string | null
}
interface Doel {
  id: string; name: string; link: string; brand: string; model_slug: string | null; external_id: string | null
  other_slug?: string | null; link_confirmed?: boolean; link_guess?: boolean
  active: boolean; gift_confirmed_by?: string | null; gift_confirmed_at?: string | null
  own_brand?: boolean; usable?: boolean; problems?: string[]
  gift_evidence?: { ok: boolean; conversations?: number; welcome_preview?: string; reason?: string } | null
}
interface EigenModel { slug: string; name: string; platform: string; link: string | null; suggested_link: string | null }
interface Extern { id: string; brand: string; name: string; link: string; gift_confirmed_by: string | null; gift_confirmed_at: string | null }
interface Limieten { per_afzender_dag: number; per_merk_uur: number; afstand_afzender_min: number; venster_van: number; venster_tot: number }
interface Config {
  brand: string; senders: string[]; candidates: Kandidaat[]; targets: Doel[]; limits: Limieten
  own_models: EigenModel[]; external: Extern[]; system_account: boolean; gift_hints: string[]
  self_promo: Record<string, Doel>
  other_brand: string; other_snapshot_at: string | null
  other_models: { slug: string; name: string; platform: string; link: string | null; suggested_link: string | null; gift_seen: boolean }[]
}
/** 21-10-2026: tijd, verzendmethode en filters van één promo (zoals bij een MM). */
interface PlanVelden {
  fc_delivery_mode?: string | null; audience_include?: string | null
  exclude_recent_minutes?: number; exclude_fan_active_minutes?: number
  exclude_timewasters?: boolean; exclude_maybe_timewasters?: boolean; exclude_spenders?: boolean
  exclude_new_followers?: boolean; exclude_private_chats?: boolean; online_only?: boolean
}
interface PromoRij extends PlanVelden {
  id: number; sender: string; sender_name: string; platform: string; scheduled_at: string; status: string
  content: string; target: string | null; link: string | null; gift_hint: string | null; error: string | null
  phase?: number | null; kind?: string
}
/** Wat het planvenster nodig heeft (een rij uit Review of uit Overview → Scheduled). */
interface PlanRij extends PlanVelden {
  id: number; status: string; platform: string; scheduled_at: string | null; naam: string
}
interface Plan { planned: number; skipped: { slug: string; reason: string; day?: string }[]; drafts?: number; empty?: number
  slots: { sender: string; utc: string; target: string; kind?: string; phase?: number | null; gift_hint: string | null; text?: string | null; reasons?: string[] }[] }

const uur = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`

/** 21-10-2026 (Jordy): dezelfde opzet als Mass Messages: tabbladen, Overview eerst (Sent / Scheduled). */
const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'review', label: 'Review' },
  { value: 'setup', label: 'Setup' },
]

export default function PromoMM() {
  const zone = useMijnZone()
  const [zoek, setZoek] = useSearchParams()
  const [stand, setStand] = useState<{ dirty: boolean; afzenders: number }>({ dirty: false, afzenders: 0 })
  const [reviewVersie, setReviewVersie] = useState(0)
  const verversReview = useCallback(() => setReviewVersie((v) => v + 1), [])
  // Edit in Overview → Scheduled: het promo-planvenster (de lijst ververst na opslaan via de sleutel).
  const [plan, setPlan] = useState<{ rij: PlanRij; ververs: () => void } | null>(null)
  const cfg = useApi<Config>(`${P}/config`)
  if (cfg.loading && !cfg.data) return <Loading />
  if (cfg.error) return <ErrorNote error={cfg.error} />
  const c = cfg.data
  if (!c) return <Loading />
  const tab = TABS.some((t) => t.value === zoek.get('tab')) ? (zoek.get('tab') as string) : 'overview'
  const setTab = (v: string) => setZoek(v === 'overview' ? {} : { tab: v }, { replace: true })
  return (
    <div data-testid="promo-mm">
      <div className="filters">
        <Seg options={TABS} value={tab} onChange={setTab} />
      </div>
      {!c.system_account ? (
        <div className="note" data-testid="promo-geen-account" style={{ marginBottom: 14 }}>
          The system account for promo Mass Messages has not been set up yet. Generating is possible once it exists.
        </div>
      ) : null}
      {tab === 'overview' ? (
        <Overzicht zone={zone} bron="promo"
                   onBewerk={(g: GeplandRij, ververs) => setPlan({ rij: { ...g, naam: g.model_name || g.model_slug || '' }, ververs })} />
      ) : tab === 'review' ? (
        <Beoordelen zone={zone} versie={reviewVersie} />
      ) : (
        <div className="col" style={{ gap: 14 }}>
          <Instellingen c={c} onKlaar={cfg.refetch} onStand={setStand} />
          <Genereren c={c} zone={zone} stand={stand} onKlaar={verversReview} />
        </div>
      )}
      {plan ? (
        <PromoPlan rij={plan.rij} zone={zone} onSluit={() => setPlan(null)}
                   onOpgeslagen={() => { plan.ververs(); setPlan(null) }} />
      ) : null}
    </div>
  )
}

/**
 * 21-10-2026 (Jordy): tijd en MM-opties van één promo, met hetzelfde gedrag als een gewone MM.
 * Standaard Platform Mass DM naar de hele volgersgroep (brede reach); een segment of uitsluiting maakt
 * het een Dashboard Mass Message (alleen fans met een gesprek). "Save" bewaart (de status blijft);
 * "Save & schedule" plant een concept of goedgekeurde promo meteen in. Er gaat niets uit tot zijn tijd.
 */
function PromoPlan({ rij, zone, onSluit, onOpgeslagen }: {
  rij: PlanRij; zone: string; onSluit: () => void; onOpgeslagen: () => void
}) {
  const { run, busy } = useActions()
  const [m, setM] = useState<DatumTijd>(() => zoneDelen(rij.scheduled_at, zone) ?? standaardMoment(Date.now(), zone))
  const [w, setW] = useState(() => ({
    doelgroep: rij.audience_include || 'all',
    recent: Number(rij.exclude_recent_minutes || 0),
    fanActief: Number(rij.exclude_fan_active_minutes || 0),
    geenTimewasters: Boolean(rij.exclude_timewasters),
    geenMaybe: Boolean(rij.exclude_maybe_timewasters),
    geenBetalers: Boolean(rij.exclude_spenders),
    geenNieuwe: Boolean(rij.exclude_new_followers),
    geenPrive: Boolean(rij.exclude_private_chats),
    alleenOnline: Boolean(rij.online_only),
  }))
  // Zonder filter: wat er gekozen was (een direct gemaakte promo zonder filter blijft direct); standaard native.
  const [gekozen, setGekozen] = useState<'native' | 'direct'>(
    () => (rij.fc_delivery_mode === 'direct' && !heeftFilters(w) ? 'direct' : 'native'))
  const gefilterd = heeftFilters(w)
  const methode = gefilterd ? 'direct' : gekozen
  const fout = tijdFout(m.datum, m.tijd, undefined, zone)
  const ingepland = rij.status === 'scheduled'
  const bewaar = (schedule: boolean) => {
    const wanneer = zoneNaarUtc(m.datum, m.tijd, zone)
    if (!wanneer) return
    void run({
      label: schedule ? 'Schedule promo' : 'Save promo plan',
      confirm: schedule
        ? `Schedule this promo for ${alsZone(wanneer, zone)} (${zoneLabel(zone)})? It goes out at that time as a `
          + `${methodeNaam(methode)}${gefilterd ? ' (only fans who have a conversation)' : ' to the whole follower group'}. `
          + 'It is checked again first.'
        : undefined,
      request: () => api.patch(`${P}/drafts/${rij.id}/plan`, {
        scheduled_at: wanneer, fc_delivery_mode: methode, audience_include: w.doelgroep,
        exclude_recent_minutes: w.recent, exclude_fan_active_minutes: w.fanActief,
        exclude_timewasters: w.geenTimewasters, exclude_maybe_timewasters: w.geenMaybe,
        exclude_spenders: w.geenBetalers, exclude_new_followers: w.geenNieuwe,
        exclude_private_chats: w.geenPrive, online_only: w.alleenOnline, schedule,
      }),
      successText: schedule ? 'Scheduled. It goes out at its time.' : (ingepland ? 'Saved; still scheduled.' : 'Saved.'),
      onDone: onOpgeslagen,
    })
  }
  return (
    <Modal title={`Plan promo · ${rij.naam}`} width={620} onClose={onSluit}
           desc={ingepland ? 'Scheduled: change the time or options until 3 minutes before sending.'
             : 'Nothing is sent until you schedule it (here, or Approve → Send approved).'}
           footer={
             <>
               <button className="btn ghost sm" type="button" onClick={onSluit}>Cancel</button>
               <button className={ingepland ? 'btn primary sm' : 'btn sm'} type="button" data-testid="promo-plan-opslaan"
                       disabled={busy !== null || Boolean(fout)} onClick={() => bewaar(false)}>Save</button>
               {!ingepland ? (
                 <button className="btn primary sm" type="button" data-testid="promo-plan-inplannen"
                         disabled={busy !== null || Boolean(fout)} onClick={() => bewaar(true)}>Save &amp; schedule</button>
               ) : null}
             </>
           }>
      <div className="col" style={{ gap: 10 }} data-testid="promo-plan">
        <TijdKiezer datum={m.datum} tijd={m.tijd} zone={zone} naam="Promo"
                    label={`Date and time (${zoneLabel(zone)})`} onChange={setM} />
        {fout ? <div className="massa-slot-errors">{fout}</div> : null}
        <Field label="Method">
          <Seg options={[{ value: 'native', label: 'Platform Mass DM' }, { value: 'direct', label: 'Dashboard Mass Message' }]}
               value={methode} onChange={(v) => setGekozen(v as 'native' | 'direct')} />
        </Field>
        <Field label="Send to">
          <select className="inp" value={w.doelgroep} aria-label="Send to"
                  onChange={(e) => setW({ ...w, doelgroep: e.target.value })}>
            {DOELGROEPEN.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </Field>
        <Field label="Exclude fans active (their own messages)">
          <select className="inp" value={String(w.fanActief)} aria-label="Exclude fans active"
                  onChange={(e) => setW({ ...w, fanActief: Number(e.target.value) })}>
            {FAN_ACTIEF.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        <Field label="Exclude recent replies (ours)">
          <select className="inp" value={String(w.recent)} aria-label="Exclude recent replies"
                  onChange={(e) => setW({ ...w, recent: Number(e.target.value) })}>
            {RECENT.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        <div className="field">
          <label>Exclusions</label>
          <div className="uitsluitingen">
            {UITSLUITINGEN.map(([label, veld]) => (
              <label className="chkrow" key={label}>
                <span className={w[veld] ? 'chk on' : 'chk'} onClick={() => setW({ ...w, [veld]: !w[veld] })}>
                  {w[veld] ? '✓' : ''}
                </span>
                {label}
              </label>
            ))}
          </div>
        </div>
        <div className="field" data-testid="mm-filter-afweging">
          {gefilterd ? (
            <span className="note">
              Filtered promo: sent <b>directly, one by one</b> (Dashboard Mass Message). It reaches
              <b> only fans who have a conversation</b>, not all followers, and takes about 2 seconds per fan.
              {gekozen === 'native' ? ' The Platform Mass DM is switched off for this promo because of the filter.' : ''}
            </span>
          ) : (
            <span className="hint">
              {methode === 'native'
                ? 'No segment or exclusion: Platform Mass DM to the whole follower group (widest reach, usual for a promo). Choosing a filter above makes it a direct message.'
                : 'No segment or exclusion: sent to all fans who have a conversation.'}
            </span>
          )}
        </div>
      </div>
    </Modal>
  )
}

function Instellingen({ c, onKlaar, onStand }: {
  c: Config; onKlaar: () => void; onStand: (s: { dirty: boolean; afzenders: number }) => void
}) {
  const { run, busy } = useActions()
  const [bewerkLink, setBewerkLink] = useState<string | null>(null)
  const [afz, setAfz] = useState<string[]>(c.senders)
  // Een doel = alleen een verwijzing naar een model (dit merk) of een opgeslagen model van het andere merk.
  const [doelen, setDoelen] = useState<{ id: string; model_slug: string | null; external_id: string | null; other_slug: string | null; active: boolean }[]>([])
  const [links, setLinks] = useState<Record<string, string>>({})
  const [extern, setExtern] = useState<Extern[]>(c.external)
  const [nieuwExtern, setNieuwExtern] = useState<{ name: string; link: string } | null>(null)
  const [lim, setLim] = useState<Limieten>(c.limits)
  // 20-10-2026 (Jordy): per afzender een optionele self-promo (migratie naar het eigen nieuwe account): 2 per dag.
  const zelfUit = (z: Record<string, Doel>) => Object.fromEntries(Object.entries(z).map(([k, d]) =>
    [k, d.model_slug ? `eigen:${d.model_slug}` : d.other_slug ? `ander:${d.other_slug}` : '']))
  const [zelf, setZelf] = useState<Record<string, string>>(zelfUit(c.self_promo ?? {}))
  const ander = c.other_brand || (c.brand === 'nt' ? 'tt' : 'nt')
  useEffect(() => {
    setAfz(c.senders); setLim(c.limits); setExtern(c.external); setZelf(zelfUit(c.self_promo ?? {}))
    setDoelen(c.targets.map((d) => ({ id: d.id, model_slug: d.model_slug, external_id: d.external_id, other_slug: d.other_slug ?? null, active: d.active })))
    setLinks(Object.fromEntries([
      ...c.own_models.filter((m) => m.link).map((m) => [m.slug, m.link as string]),
      ...c.other_models.filter((m) => m.link).map((m) => [`${c.other_brand}:${m.slug}`, m.link as string]),
    ]))
  }, [c])
  const eigen = useMemo(() => Object.fromEntries(c.own_models.map((m) => [m.slug, m])), [c.own_models])
  const anderen = useMemo(() => Object.fromEntries(c.other_models.map((m) => [m.slug, m])), [c.other_models])
  const stand = useMemo(() => Object.fromEntries(c.targets.map((d) => [d.id, d])), [c.targets])
  const opslaan = (extra?: { external?: Extern[]; targets?: typeof doelen }) => run({
    label: 'Save promo settings',
    request: () => api.post(`${P}/config`, {
      senders: afz, limits: lim, model_links: links,
      self_promo: Object.fromEntries(Object.entries(zelf).filter(([, v]) => v).map(([k, v]) => {
        const [soort, sleutel] = v.split(':')
        return [k, { model_slug: soort === 'eigen' ? sleutel : null, other_slug: soort === 'ander' ? sleutel : null, active: true }]
      })),
      external: (extra?.external ?? extern).map(({ id, brand, name, link }) => ({ id: id || null, brand, name, link })),
      targets: (extra?.targets ?? doelen).map((d) => ({ id: d.id || null, model_slug: d.model_slug, external_id: d.external_id, other_slug: d.other_slug, active: d.active })),
    }),
    successText: 'Saved.',
    onDone: onKlaar,
  })
  const voegToe = (waarde: string) => {
    if (!waarde) return
    if (waarde === '__nieuw') { setNieuwExtern({ name: '', link: '' }); return }
    const [soort, sleutel] = waarde.split(':')
    setDoelen([...doelen, { id: '', model_slug: soort === 'eigen' ? sleutel : null, external_id: soort === 'extern' ? sleutel : null,
                            other_slug: soort === 'ander' ? sleutel : null, active: true }])
  }
  const bewaarNieuwExtern = () => {
    if (!nieuwExtern || !nieuwExtern.name.trim()) return
    const id = `x${Date.now().toString(36)}`
    const nieuweLijst = [...extern, { id, brand: ander, name: nieuwExtern.name.trim(), link: nieuwExtern.link.trim(), gift_confirmed_by: null, gift_confirmed_at: null }]
    const nieuweDoelen = [...doelen, { id: '', model_slug: null, external_id: id, other_slug: null, active: true }]
    setExtern(nieuweLijst); setDoelen(nieuweDoelen); setNieuwExtern(null)
    void opslaan({ external: nieuweLijst, targets: nieuweDoelen })
  }
  // Niet-opgeslagen wijzigingen: Generate werkt met de opgeslagen instellingen en zegt dat dan.
  const opgeslagen = useMemo(() => JSON.stringify({
    a: [...c.senders].sort(), d: c.targets.map((d) => [d.model_slug, d.external_id, d.other_slug ?? null, d.active]),
    z: Object.entries(zelfUit(c.self_promo ?? {})).filter(([, v]) => v).sort(),
    l: lim === c.limits ? null : c.limits,
  }), [c, lim])
  const nu = JSON.stringify({
    a: [...afz].sort(), d: doelen.map((d) => [d.model_slug, d.external_id, d.other_slug, d.active]),
    z: Object.entries(zelf).filter(([, v]) => v).sort(),
    l: lim === c.limits ? null : lim,
  })
  const linksGewijzigd = Object.entries(links).some(([k, v]) => {
    const eigenM = c.own_models.find((m) => m.slug === k)
    const andereM = c.other_models.find((m) => `${c.other_brand}:${m.slug}` === k)
    return (eigenM ? eigenM.link : andereM ? andereM.link : null) !== v
  })
  const dirty = nu !== opgeslagen || linksGewijzigd
  useEffect(() => { onStand({ dirty, afzenders: afz.length }) }, [dirty, afz.length, onStand])
  const gekozen = new Set(doelen.map((d) => d.model_slug ?? (d.other_slug ? `o:${d.other_slug}` : `x:${d.external_id}`)))
  const zichtbaar = c.candidates.filter((k) => k.hidden || k.removed || !k.active || afz.includes(k.slug))
  const rest = c.candidates.filter((k) => !zichtbaar.includes(k))
  const [alles, setAlles] = useState(false)
  return (
    <>
      <Panel title="Senders" desc="Whose follower base is used. Hidden and inactive models are listed too; a model that can't send is marked with the reason.">
        <Table cols={[{ key: 's', label: '' }, { key: 'm', label: 'Model' }, { key: 'st', label: 'State' },
                      { key: 'f', label: 'Followers', num: true }, { key: 'l', label: 'Last Mass DM' }, { key: 'u', label: 'Can send' },
                      { key: 'z', label: 'Self-promo (own new account)' }]}
               emptyText="No models.">
          {(alles ? c.candidates : zichtbaar).map((k) => (
            <tr key={k.slug} data-testid="promo-afzender">
              <td><input type="checkbox" aria-label={`Sender ${k.name}`} disabled={!k.usable && !afz.includes(k.slug)}
                         checked={afz.includes(k.slug)}
                         onChange={(e) => setAfz(e.target.checked ? [...afz, k.slug] : afz.filter((s) => s !== k.slug))} /></td>
              <td>{k.name} <span className="mut2">({k.platform === 'f2f' ? 'F2F' : 'FC'})</span></td>
              <td>{k.removed ? <Tag kind="bad">removed</Tag> : !k.active ? <Tag kind="warn">inactive</Tag> : k.hidden ? <Tag kind="mut">hidden</Tag> : <Tag kind="ok">active</Tag>}</td>
              <td className="num">{k.followers ?? '—'}</td>
              <td>{k.last_sent ? k.last_sent.slice(8, 10) + '-' + k.last_sent.slice(5, 7) + '-' + k.last_sent.slice(0, 4) : '—'}</td>
              <td>{k.usable ? '✓' : <span className="mut2" title={k.reason ?? ''}>✗ {k.reason}</span>}</td>
              <td data-testid="promo-zelf">{afz.includes(k.slug) ? (<>
                <select value={zelf[k.slug] ?? ''} aria-label={`Self-promo ${k.name}`}
                        onChange={(e) => setZelf({ ...zelf, [k.slug]: e.target.value })}>
                  <option value="">—</option>
                  <optgroup label={`${c.brand.toUpperCase()} models`}>
                    {c.own_models.map((m) => <option key={m.slug} value={`eigen:${m.slug}`}>{m.name}</option>)}
                  </optgroup>
                  {c.other_models.length ? (
                    <optgroup label={`${(c.other_brand || '').toUpperCase()} models`}>
                      {c.other_models.map((m) => <option key={m.slug} value={`ander:${m.slug}`}>{m.name}</option>)}
                    </optgroup>
                  ) : null}
                </select>
                {zelf[k.slug] ? <Tag kind="acc" title="1 promo for another model + 1 self-promo (migration) per day">2/day</Tag> : null}
                {c.self_promo?.[k.slug] ? (c.self_promo[k.slug].usable
                  ? <span className="mut2" style={{ fontSize: 11 }}> ✓ {c.self_promo[k.slug].link}{c.self_promo[k.slug].link_guess ? ' (guess, check)' : ''}</span>
                  : <span className="mut2" style={{ fontSize: 11 }}> ✗ {(c.self_promo[k.slug].problems ?? []).join('; ')}</span>) : null}
              </>) : <span className="mut2">—</span>}</td>
            </tr>
          ))}
        </Table>
        {rest.length ? (
          <button className="btn ghost sm" type="button" style={{ marginTop: 8 }} onClick={() => setAlles(!alles)}>
            {alles ? 'Show only hidden/inactive and selected' : `Show all models (${c.candidates.length})`}
          </button>
        ) : null}
      </Panel>
      <Panel title="Promo targets" desc={<>Choose the models to promote; name and page link come from the model. The order matters: each sender promotes one target for 3 days, then the next one in this list. A target is ready when the model is active and has a page link; a suggested link counts (an FC link marked 'guess' is derived from the slug: check it with Edit). The gift is sent by hand by the chatters of the promoted model when a fan says who sent him.</>}>
        <Table cols={[{ key: 'n', label: 'Model' }, { key: 'b', label: 'Brand' }, { key: 'l', label: 'Page link' },
                      { key: 'a', label: 'On' }, { key: 's', label: 'Ready (active + link)' }, { key: 'x', label: '' }]}
               emptyText="No promo targets yet. Add one with the list below.">
          {doelen.map((d, i) => {
            const st = d.id ? stand[d.id] : undefined
            const o = d.other_slug ? anderen[d.other_slug] : undefined
            const m = d.model_slug ? eigen[d.model_slug] : (o ? { slug: `${ander}:${o.slug}`, name: o.name, platform: o.platform, link: o.link, suggested_link: o.suggested_link } : undefined)
            const e = d.external_id ? extern.find((x) => x.id === d.external_id) : undefined
            const naam = m?.name ?? e?.name ?? d.model_slug ?? d.other_slug ?? '?'
            return (
              <tr key={d.id || `nieuw-${i}`} data-testid="promo-doel">
                <td>{naam}</td>
                <td>{d.model_slug ? c.brand.toUpperCase() : (e?.brand ?? ander).toUpperCase()}</td>
                <td>{m ? (() => {
                  // 20-10-2026 (Jordy): de voorgestelde link telt; bevestigen hoeft niet. Een FC-gok wordt gemarkeerd.
                  const eigenLink = links[m.slug]
                  const effectief = eigenLink || st?.link || m.suggested_link || ''
                  const gok = !eigenLink && (st ? Boolean(st.link_guess) : m.platform === 'fancentro')
                  return bewerkLink === m.slug ? (
                    <span data-testid="promo-link-bewerk">
                      <input value={eigenLink ?? effectief} aria-label={`Page link ${naam}`} style={{ width: 230 }}
                             onChange={(ev) => setLinks({ ...links, [m.slug]: ev.target.value.trim() })} />
                      <button className="btn ghost sm" type="button" onClick={() => setBewerkLink(null)}>OK</button>
                    </span>
                  ) : (
                    <span data-testid="promo-link">
                      <span className="mut2">{effectief || '—'}</span>
                      {effectief && !eigenLink ? <Tag kind={gok ? 'warn' : 'mut'} title={gok ? 'Derived from the FanCentro slug: please check this link' : 'Suggested from the F2F creator name'}>{gok ? 'guess, check' : 'suggested'}</Tag> : null}
                      <button className="btn ghost sm" type="button" onClick={() => { setLinks({ ...links, [m.slug]: eigenLink || effectief }); setBewerkLink(m.slug) }}>Edit</button>
                    </span>
                  )
                })() : <span className="mut2">{e?.link || '—'}</span>}</td>
                <td><input type="checkbox" checked={d.active} aria-label="Active"
                           onChange={(ev) => setDoelen(doelen.map((x, j) => (j === i ? { ...x, active: ev.target.checked } : x)))} /></td>
                <td data-testid="promo-doel-stand">{st ? (st.usable ? <span>✓ ready</span>
                  : <span className="mut2">✗ {(st.problems ?? []).join('; ')}</span>) : <span className="mut2">save first</span>}</td>
                <td><button className="btn ghost sm" type="button" onClick={() => setDoelen(doelen.filter((_, j) => j !== i))}>Remove</button></td>
              </tr>
            )
          })}
        </Table>
        <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value="" onChange={(ev) => voegToe(ev.target.value)} aria-label="Add target" data-testid="promo-doel-toevoegen">
            <option value="">+ Add target…</option>
            <optgroup label={`${c.brand.toUpperCase()} models`}>
              {c.own_models.filter((m) => !gekozen.has(m.slug)).map((m) => <option key={m.slug} value={`eigen:${m.slug}`}>{m.name} ({m.platform === 'f2f' ? 'F2F' : 'FC'})</option>)}
            </optgroup>
            {c.other_models.length ? (
              <optgroup label={`${ander.toUpperCase()} models`}>
                {c.other_models.filter((m) => !gekozen.has(`o:${m.slug}`)).map((m) => <option key={m.slug} value={`ander:${m.slug}`}>{m.name} ({m.platform === 'f2f' ? 'F2F' : 'FC'})</option>)}
              </optgroup>
            ) : (
              <optgroup label={`${ander.toUpperCase()} models (no list from ${ander.toUpperCase()} yet: open this page there once)`}>
                {extern.filter((e) => !gekozen.has(`x:${e.id}`)).map((e) => <option key={e.id} value={`extern:${e.id}`}>{e.name}</option>)}
                <option value="__nieuw">New {ander.toUpperCase()} model…</option>
              </optgroup>
            )}
          </select>
          {nieuwExtern ? (
            <span className="row" style={{ gap: 6, alignItems: 'center' }} data-testid="promo-nieuw-extern">
              <input placeholder="Name" value={nieuwExtern.name} aria-label="Name" style={{ width: 120 }}
                     onChange={(ev) => setNieuwExtern({ ...nieuwExtern, name: ev.target.value })} />
              <input placeholder="https://..." value={nieuwExtern.link} aria-label="Page link" style={{ width: 220 }}
                     onChange={(ev) => setNieuwExtern({ ...nieuwExtern, link: ev.target.value })} />
              <button className="btn sm" type="button" disabled={busy !== null || !nieuwExtern.name.trim()} onClick={bewaarNieuwExtern}>Add</button>
              <button className="btn ghost sm" type="button" onClick={() => setNieuwExtern(null)}>Cancel</button>
            </span>
          ) : null}
          <span className="mut2" data-testid="promo-cadans">1 promo per sender per day · 3 days per target (intro, reminder, last chance), then the next target in this list · a sender with a self-promo gets 2 per day (+1 migration message to its own new account)</span>
          <label>Promo per brand per hour <input type="number" min={1} max={6} value={lim.per_merk_uur} style={{ width: 50 }}
                 onChange={(e) => setLim({ ...lim, per_merk_uur: Number(e.target.value) })} /></label>
          <label>Min. minutes between MMs of a sender <input type="number" min={60} max={720} step={30} value={lim.afstand_afzender_min} style={{ width: 60 }}
                 onChange={(e) => setLim({ ...lim, afstand_afzender_min: Number(e.target.value) })} /></label>
          <span className="mut2">Window {uur(lim.venster_van)}–{uur(lim.venster_tot)} (sender's time zone)</span>
          <span style={{ flex: 1 }} />
          <button className="btn primary sm" type="button" disabled={busy !== null} onClick={() => { void opslaan() }}>Save promo settings</button>
        </div>
      </Panel>
    </>
  )
}

interface GenStand {
  running: boolean; interrupted?: boolean; total?: number; done?: number; current_sender?: string | null
  started_at?: string; finished_at?: string | null; error?: string | null; days?: number
  drafts?: number; empty?: number; skipped?: Plan['skipped']; slots?: Plan['slots']
}

const DAGEN = Array.from({ length: 31 }, (_, i) => i + 1)

function Genereren({ c, zone, stand, onKlaar }: {
  c: Config; zone: string; stand: { dirty: boolean; afzenders: number }; onKlaar: () => void
}) {
  const { run, busy } = useActions()
  const [moment, setMoment] = useState<DatumTijd>(() => standaardMoment(Date.now(), zone))
  const [dagen, setDagen] = useState(1)
  const [afz, setAfz] = useState<string[]>([])
  const [plan, setPlan] = useState<Plan | null>(null)
  const [fout, setFout] = useState('')
  // 20-10-2026 (Jordy): een maand vooruit; Generate draait op de achtergrond met voortgang.
  const gen = useApi<GenStand>(`${P}/generate`)
  const loopt = Boolean(gen.data?.running)
  usePoll(3000, gen.refetch, loopt)
  const liep = useRef(false)
  useEffect(() => {
    if (liep.current && !loopt) { onKlaar(); setPlan(null) }
    liep.current = loopt
  }, [loopt, onKlaar])
  const iso = zoneNaarUtc(moment.datum, moment.tijd, zone)
  const keuze = c.senders
  // 20-10-2026 (Jordy): nooit stil niets doen; zeg waarom Generate (nog) niet kan.
  const reden = !c.system_account ? 'The system account for promo Mass Messages is missing.'
    : stand.dirty ? 'Save promo settings first: you have unsaved changes above.'
    : !keuze.length ? 'No sender selected: tick at least one sender above and press Save promo settings.'
    : !c.targets.some((d) => d.usable) ? 'No usable promo target: add an active model with a page link above and press Save promo settings.'
    : !iso ? 'Pick a valid start date and time.'
    : loopt ? 'Generate is running; wait until it has finished.'
    : null
  useEffect(() => { setPlan(null) }, [iso, dagen, afz.join(',')])
  const body = () => ({ senders: afz.length ? afz : keuze, start: iso, days: dagen })
  const bekijk = async () => {
    setFout('')
    try { setPlan(await api.post<Plan>(`${P}/generate`, { ...body(), dry_run: true })) } catch (e) { setPlan(null); setFout(e instanceof Error ? e.message : String(e)) }
  }
  const maak = () => plan && run({
    label: 'Generate promo drafts',
    confirm: `Write ${plan.planned} promo draft(s) for ${dagen} day(s) now? The AI writes them on the background, in blocks per sender `
      + '(this can take a few minutes for a month). Nothing is sent; they appear below in Review (To review).',
    request: () => api.post(`${P}/generate`, body()),
    successText: 'Generate started. Progress is shown here.',
    onDone: gen.refetch,
  })
  const namen = useMemo(() => Object.fromEntries(c.candidates.map((k) => [k.slug, k.name])), [c.candidates])
  const g = gen.data
  const resultaat = !loopt && g?.slots?.length ? g : null
  const rijen = plan ? plan.slots : (resultaat?.slots ?? [])
  return (
    <Panel title="Generate" desc="Plans the times (own limits), the 3-day sequence per target and the gift wording, and lets the AI write a different text per message. The link is added by the code under the text. Up to 31 days ahead.">
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }} data-testid="promo-genereer">
        <TijdKiezer datum={moment.datum} tijd={moment.tijd} onChange={setMoment} zone={zone} naam="Promo from"
                    label={`From (${zoneLabel(zone)})`} />
        <label>Days <select value={dagen} onChange={(e) => setDagen(Number(e.target.value))} aria-label="Days">
          {DAGEN.map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
        <button className="btn ghost sm" type="button" onClick={() => setDagen(31)} data-testid="promo-maand">Whole month (31 days)</button>
        <label>Senders <select multiple value={afz} onChange={(e) => setAfz(Array.from(e.target.selectedOptions).map((o) => o.value))}
                               aria-label="Senders for this run" style={{ minWidth: 180, height: 70 }}>
          {keuze.map((s) => <option key={s} value={s}>{namen[s] ?? s}</option>)}</select></label>
        <span className="mut2" style={{ fontSize: 12 }}>{afz.length ? `${afz.length} selected` : `all ${keuze.length} selected senders`}</span>
        <button className="btn sm" type="button" disabled={reden !== null || busy !== null} onClick={() => { void bekijk() }}>Preview plan</button>
        <button className="btn primary sm" type="button" disabled={!plan || !plan.planned || loopt || busy !== null}
                onClick={() => { void maak() }}>Generate {plan ? plan.planned : ''} drafts</button>
      </div>
      {reden ? <div className="note" style={{ marginTop: 8 }} data-testid="promo-genereer-reden">{reden}</div> : null}
      {fout ? <div className="note" style={{ marginTop: 8 }} data-testid="promo-genereer-fout">{fout}</div> : null}
      {loopt && g ? (
        <div style={{ marginTop: 10 }} data-testid="promo-voortgang">
          <div className="mut2" style={{ fontSize: 12, marginBottom: 4 }}>
            Writing drafts… {g.done ?? 0} / {g.total ?? 0}{g.current_sender ? ` · now: ${namen[g.current_sender] ?? g.current_sender}` : ''}
          </div>
          <div style={{ height: 6, background: 'var(--line, #333)', borderRadius: 3 }}>
            <div style={{ height: 6, width: `${g.total ? Math.round(100 * (g.done ?? 0) / g.total) : 0}%`, background: 'var(--acc, #8b5cf6)', borderRadius: 3 }} />
          </div>
        </div>
      ) : null}
      {!loopt && g?.error ? <div className="note" style={{ marginTop: 8 }}>The last Generate stopped with an error: {g.error}</div> : null}
      {!loopt && g?.interrupted ? <div className="note" style={{ marginTop: 8 }}>The last Generate was interrupted; the drafts written so far are in Review.</div> : null}
      {plan || resultaat ? (
        <div style={{ marginTop: 10 }} data-testid="promo-plan">
          <div className="mut2" style={{ fontSize: 12, marginBottom: 6 }}>
            {plan ? `${plan.planned} planned (preview: the AI writes the texts when you press Generate)`
              : `Last Generate: ${resultaat?.drafts ?? 0} draft(s) made, ${resultaat?.empty ?? 0} empty${resultaat?.finished_at ? ` · ${alsZone(resultaat.finished_at, zone)}` : ''}`}
            {(plan?.skipped ?? resultaat?.skipped ?? []).length ? ` · skipped: ${(plan?.skipped ?? resultaat?.skipped ?? []).map((s) => `${namen[s.slug] ?? s.slug}${s.day ? ' ' + s.day : ''} (${s.reason})`).join('; ')}` : ''}
          </div>
          <Table cols={[{ key: 't', label: 'When' }, { key: 's', label: 'Sender' }, { key: 'd', label: 'Promotes' }, { key: 'g', label: 'Gift wording' }, { key: 'x', label: 'Text' }]}>
            {rijen.map((s, i) => (
              <tr key={i} data-testid="promo-plan-rij"><td>{alsZone(s.utc, zone)}</td><td>{namen[s.sender] ?? s.sender}</td><td>{s.kind === 'self' ? <span>self-promo → {s.target}</span> : s.target}{s.phase ? <span className="mut2"> · day {s.phase}/3</span> : null}</td><td>{s.gift_hint ?? '—'}</td>
                <td>{s.text ? s.text : plan ? <span className="mut2">written by the AI when you press Generate</span>
                  : s.reasons?.length ? <span className="mut2">empty: {s.reasons.join(', ')}</span> : <span className="mut2">—</span>}</td></tr>
            ))}
          </Table>
        </div>
      ) : null}
    </Panel>
  )
}

// 21-10-2026: Scheduled en Sent staan in Overview (zoals bij Mass Messages).
const STATUSSEN = [{ value: 'draft', label: 'To review' }, { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' }]

function Beoordelen({ zone, versie }: { zone: string; versie: number }) {
  const { run, busy, notify } = useActions()
  const [status, setStatus] = useState('draft')
  const q = useApi<{ items: PromoRij[]; total: number }>(`${P}/drafts?status=${status}`, [versie])
  const [bewerk, setBewerk] = useState<{ id: number; tekst: string } | null>(null)
  const [planRij, setPlanRij] = useState<PlanRij | null>(null)
  const actie = (r: PromoRij, wat: 'approve' | 'reject') => run({
    label: wat === 'approve' ? 'Approve' : 'Reject',
    request: () => api.post(`${P}/drafts/${r.id}/${wat}`, {}),
    successText: wat === 'approve' ? 'Approved.' : 'Rejected.',
    onDone: q.refetch,
  })
  const opslaan = () => bewerk && run({
    label: 'Save text',
    request: () => api.patch(`${P}/drafts/${bewerk.id}`, { text: bewerk.tekst }),
    successText: 'Saved; back in To review.',
    onDone: () => { setBewerk(null); q.refetch() },
  })
  const plan = async () => {
    const n = (await api.post<{ count: number }>(`${P}/send-approved`, { expected_count: 0, dry_run: true })).count
    await run({
      label: 'Send approved',
      confirm: `Schedule ${n} approved promo Mass Message(s)? Each goes out at its time from the sender's account to all its followers. Every message is checked again first.`,
      request: () => api.post(`${P}/send-approved`, { expected_count: n }),
      successText: 'Scheduled.',
      onDone: q.refetch,
    })
  }
  const items = q.data?.items ?? []
  // 21-10-2026 (Jordy): alle concepten in To review in één klik goedkeuren. Goedkeuren is niet verzenden.
  const allesGoed = () => {
    const ids = items.map((r) => r.id)
    return run({
      label: 'Approve all',
      confirm: `Approve ${ids.length} draft(s)? Approving does not send anything: nothing goes out until you press Send approved.`,
      request: async () => {
        const r = await api.post<{ approved: number; skipped: unknown[]; not_draft_anymore: number }>(
          `${P}/drafts/approve-all`, { ids })
        const rest = r.skipped.length + r.not_draft_anymore
        if (rest) notify(`${r.approved} approved; ${rest} not (still in To review or no longer a draft).`, false)
      },
      successText: 'Approved. Nothing is sent until Send approved.',
      onDone: q.refetch,
    })
  }
  return (
    <Panel title="Review" desc={`Times in ${zoneLabel(zone)}. Nothing goes out before you press Send approved.`}
           right={<button className="btn ghost sm" type="button" onClick={() => q.refetch()}>Refresh</button>}>
      <div className="row" style={{ gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Seg options={STATUSSEN} value={status} onChange={setStatus} />
        <span style={{ flex: 1 }} />
        {status === 'draft' && items.length ? (
          <button className="btn primary sm" type="button" disabled={busy !== null} data-testid="promo-approve-all"
                  onClick={() => { void allesGoed() }}>Approve all ({items.length})</button>) : null}
        {status === 'approved' && items.length ? (
          <button className="btn primary sm" type="button" disabled={busy !== null} data-testid="promo-send-approved"
                  onClick={() => { void plan() }}>Send approved ({items.length})</button>) : null}
      </div>
      {q.loading && !q.data ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : !items.length ? (
        <Empty>No promo messages in this list.</Empty>
      ) : (
        <Table cols={[{ key: 't', label: 'When' }, { key: 's', label: 'Sender' }, { key: 'd', label: 'Promotes' }, { key: 'x', label: 'Message' }, { key: 'a', label: '' }]}>
          {items.map((r) => (
            <tr key={r.id} data-testid="promo-rij">
              <td>
                {alsZone(r.scheduled_at, zone)}
                {/* 21-10-2026: verzendmethode zoals bij een MM (standaard Platform Mass DM, hele volgersgroep). */}
                <div className="mut2" style={{ fontSize: 11 }} data-testid="promo-methode">
                  {r.fc_delivery_mode === 'direct' ? 'Dashboard Mass Message' : 'Platform Mass DM'}
                  {r.audience_include && r.audience_include !== 'all' ? ` · ${r.audience_include}` : ''}
                </div>
              </td>
              <td>{r.sender_name}</td>
              <td>{r.kind === 'self' ? <span>self-promo → {r.target}</span> : (r.target ?? '—')}{r.phase ? <span className="mut2"> · day {r.phase}/3</span> : null}{r.gift_hint ? <div className="mut2" style={{ fontSize: 11 }}>{r.gift_hint}</div> : null}</td>
              <td style={{ whiteSpace: 'pre-wrap', maxWidth: 520 }}>
                {bewerk?.id === r.id ? (
                  <textarea value={bewerk.tekst} rows={4} style={{ width: '100%' }} aria-label="Promo text"
                            onChange={(e) => setBewerk({ id: r.id, tekst: e.target.value })} />
                ) : r.content}
                {r.error ? <div className="mut2">{r.error}</div> : null}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {bewerk?.id === r.id ? (<>
                  <button className="btn primary sm" type="button" disabled={busy !== null} onClick={() => { void opslaan() }}>Save</button>
                  <button className="btn ghost sm" type="button" onClick={() => setBewerk(null)}>Cancel</button>
                </>) : (status === 'draft' || status === 'approved') ? (<>
                  {status === 'draft' ? <button className="btn sm" type="button" disabled={busy !== null} onClick={() => { void actie(r, 'approve') }}>Approve</button> : null}
                  <button className="btn ghost sm" type="button" data-testid="promo-plan-knop"
                          onClick={() => setPlanRij({ ...r, naam: r.sender_name })}>Plan</button>
                  <button className="btn ghost sm" type="button" onClick={() => setBewerk({ id: r.id, tekst: r.link ? r.content.replace(r.link, '').trim() : r.content })}>Edit</button>
                  <button className="btn ghost sm" type="button" disabled={busy !== null} onClick={() => { void actie(r, 'reject') }}>Reject</button>
                </>) : null}
              </td>
            </tr>
          ))}
        </Table>
      )}
      {planRij ? (
        <PromoPlan rij={planRij} zone={zone} onSluit={() => setPlanRij(null)}
                   onOpgeslagen={() => { setPlanRij(null); q.refetch() }} />
      ) : null}
    </Panel>
  )
}
